import React, { useEffect, useState, useRef } from 'react';
import chatService from '../services/chatService';
import FileDropzone from '../components/FileDropzone';
import { formatTimestamp } from '../utils/dateUtils';
import { textToHtml } from '../components/SummaryEditor';
import './ChatPage.css';

const POLL_INTERVAL = 15000; // ms for pending deep responses (UI level polling)

const ChatPage = () => {
  // models will be { quick: { openai: [], anthropic: [] }, deep: { openai: [], anthropic: [] }, default: '' }
  const [models, setModels] = useState({ quick: { openai: [], anthropic: [] }, deep: { openai: [], anthropic: [] }, default: '' });
  const [provider, setProvider] = useState('openai');
  const [model, setModel] = useState('');
  const [mode, setMode] = useState('quick');
  const [message, setMessage] = useState('');
  const [conversationId, setConversationId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [conversationName, setConversationName] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [loadingSend, setLoadingSend] = useState(false);
  const [clarifyQuestions, setClarifyQuestions] = useState('');
  const [clarifyInput, setClarifyInput] = useState('');
  const [enhancedText, setEnhancedText] = useState('');
  const [enhancing, setEnhancing] = useState(false);
  const [pendingOriginalMessage, setPendingOriginalMessage] = useState('');
  const [enhanceView, setEnhanceView] = useState('preview'); // 'edit' or 'preview' — default to preview (sanitized)
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [showConversations, setShowConversations] = useState(true);
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [wizardStep, setWizardStep] = useState(1); // 1: Compose, 2: Clarify, 3: Enhance
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [conversationSearch, setConversationSearch] = useState('');
  const [conversationSort, setConversationSort] = useState('date'); // 'date' or 'title'
  const [expandedMessages, setExpandedMessages] = useState({}); // Track which messages are expanded
  const pendingMap = useRef({});
  const messagesContainerRef = useRef(null);
  const modelFreezeRef = useRef(false);

  useEffect(() => {
  // On mount, just set up polling and load conversations. Model fetching
  // is handled by the `mode` effect so we don't duplicate requests.
  loadConversations();
    const interval = setInterval(() => pollPending(), POLL_INTERVAL);
    return () => clearInterval(interval);
  }, []);

  // When provider changes, update the selected model to a provider-appropriate choice
  useEffect(() => {
  const providerModels = provider === 'openai' ? models[mode]?.openai : (provider === 'anthropic' ? models[mode]?.anthropic : []);
    if (!providerModels || providerModels.length === 0) {
      // nothing available for this provider
      return;
    }
    // If the clarify/enhance flow is active, avoid overwriting the user's model
    if (modelFreezeRef.current) return;
    if (!model || !providerModels.includes(model)) {
      // prefer overall default if present and applicable, otherwise first provider model
      setModel(models.default && providerModels.includes(models.default) ? models.default : providerModels[0]);
    }
  }, [provider]);

  // When mode changes, fetch the models for that mode and update the dropdown.
  useEffect(() => {
    const fetchForMode = async () => {
      try {
        const m = await chatService.getModels();
        setModels({
          quick: { openai: Array.isArray(m.quick?.openai) ? m.quick.openai : [], anthropic: Array.isArray(m.quick?.anthropic) ? m.quick.anthropic : [] },
          deep: { openai: Array.isArray(m.deep?.openai) ? m.deep.openai : [], anthropic: Array.isArray(m.deep?.anthropic) ? m.deep.anthropic : [] },
          default: m.default || ''
        });
        // Don't overwrite the user's selection while clarify/enhance is pending
        if (modelFreezeRef.current) return;
        const providerModels = provider === 'openai' ? (Array.isArray(m[mode]?.openai) ? m[mode].openai : []) : (provider === 'anthropic' ? (Array.isArray(m[mode]?.anthropic) ? m[mode].anthropic : []) : []);
        // If current model is empty or not available for current provider, pick default
        if (!providerModels.includes(model)) {
          setModel(providerModels.length ? providerModels[0] : m.default);
        }
      } catch (e) {
        console.error('Failed to load models for mode', mode, e);
      }
    };
    fetchForMode();
  }, [mode]);

  // We fetch models once on page load; use the refresh button to force reload if needed.

  const loadConversations = async (force = false) => {
    try { const data = await chatService.listConversationsCached(force); setConversations(data); } catch (e) { console.error(e); }
  }

  const loadConversationMessages = async (cid) => {
    try { const data = await chatService.getConversation(cid); setMessages(data.messages); setConversationId(cid); } catch (e) { console.error(e); }
  }

  // Try to extract a conversation name from messages (most recent system message or message field)
  useEffect(() => {
    if (!messages || messages.length === 0) { setConversationName(''); return; }
    // Look for the most recent message that has conversation_name
    let name = '';
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.meta && m.meta.conversation_name) { name = m.meta.conversation_name; break; }
      if (m.conversation_name) { name = m.conversation_name; break; }
    }
    setConversationName(name || '');
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    
    // If deep mode is selected and we're on step 1, move to clarification step
    if (mode === 'deep' && wizardStep === 1) {
      try {
        setLoadingSend(true);
        // Ask backend for clarification questions using the quick/default agent
        const clar = await chatService.getClarification(message, 'quick').catch(() => null);
        setLoadingSend(false);

        if (clar && clar.questions) {
          setClarifyQuestions(clar.questions);
        } else if (clar && clar.clarification) {
          // support alternate payloads
          setClarifyQuestions(clar.clarification);
        } else {
          // fallback: simple prompt
          setClarifyQuestions('Would you like to provide any additional details or constraints for this request?');
        }
        setClarifyInput('');
        setWizardStep(2); // Move to clarify step
        return; // wait for user to continue
      } catch (err) {
        console.error('Clarification lookup failed', err);
        // proceed with send if clarification step fails
      }
    }
    
    // For quick mode, just send directly
    if (mode === 'quick') {
      await finalSendMessage(false);
    }
  }

  const continueSendWithClarification = async () => {
    let combinedMessage = message;
    if (clarifyInput && clarifyInput.trim()) {
      combinedMessage = `${message}\n\nAdditional context: ${clarifyInput.trim()}`;
    }
    // keep a copy so we can revert if user goes back
    setPendingOriginalMessage(message);
    modelFreezeRef.current = true;
    setMessage(combinedMessage);
    // Move to enhancement step
    setWizardStep(3);
    // Auto-run enhancement
    runEnhancement(combinedMessage);
  };

  const runEnhancement = async (inputText) => {
    setEnhancing(true);
    try {
      const resp = await chatService.getEnhancement(inputText || message, 'quick').catch(() => null);
      const text = resp?.enhanced || resp?.enhancement || '';
      setEnhancedText(text || '');
    } catch (e) {
      console.error('Enhancement failed', e);
      setEnhancedText('');
    } finally {
      setEnhancing(false);
    }
  };

  const goBackInWizard = () => {
    if (wizardStep === 3) {
      // Going back from enhance to clarify
      setMessage(pendingOriginalMessage || message);
      setEnhancedText('');
      setWizardStep(2);
    } else if (wizardStep === 2) {
      // Going back from clarify to compose
      setMessage(pendingOriginalMessage || message);
      setClarifyInput('');
      setPendingOriginalMessage('');
      modelFreezeRef.current = false;
      setWizardStep(1);
    }
  };

  const finalSendMessage = async (useEnhanced) => {
    const finalMessage = useEnhanced && enhancedText ? enhancedText : message;
    setMessage(finalMessage);
    setPendingOriginalMessage('');
    modelFreezeRef.current = false;
    
    // Close wizard
    setShowComposeModal(false);
    setWizardStep(1);
    
    // Now do the actual send
    setLoadingSend(true);
    try {
      // If files are attached, inline-encode them and send as inline_files (no upload)
      let inlineFiles = [];
      if (selectedFiles && selectedFiles.length > 0) {
        setUploadingFiles(true);
        setUploadStatus(`Encoding ${selectedFiles.length} file(s)...`);
        try {
          const readFileAsBase64 = (file) => new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onerror = () => { reader.abort(); reject(new DOMException('Problem parsing input file.')); };
            reader.onload = () => {
              const result = reader.result || '';
              const comma = result.indexOf(',');
              const b64 = comma >= 0 ? result.slice(comma + 1) : result;
              resolve(b64);
            };
            reader.readAsDataURL(file);
          });

          const promises = selectedFiles.map(f => readFileAsBase64(f).then(b64 => ({ filename: f.name, mime: f.type || 'application/octet-stream', b64 })));
          inlineFiles = await Promise.all(promises);
        } catch (err) {
          console.error('Failed to encode files', err);
        } finally {
          setUploadingFiles(false);
          setUploadStatus(null);
        }
      }

      const payload = { conversation_id: conversationId, conversation_name: conversationName || undefined, message: finalMessage, model, provider, mode, context_file_ids: [], inline_files: inlineFiles };
      const resp = await chatService.sendMessage(payload);
      if (!conversationId) setConversationId(resp.conversation_id);
      if (mode === 'quick') {
        setMessages(prev => [...prev, { conversation_id: resp.conversation_id, message_id: 'temp-user-' + Date.now(), role: 'user', content: finalMessage, created_at: Math.floor(Date.now() / 1000) }, { conversation_id: resp.conversation_id, message_id: resp.message_id, role: 'assistant', content: resp.response, created_at: Math.floor(Date.now() / 1000) }]);
      } else {
        setMessages(prev => [...prev, { conversation_id: resp.conversation_id, message_id: 'temp-user-' + Date.now(), role: 'user', content: finalMessage, created_at: Math.floor(Date.now() / 1000) }, { conversation_id: resp.conversation_id, message_id: resp.message_id, role: 'assistant', content: '(pending deep response)', created_at: Math.floor(Date.now() / 1000), pending: true, response_id: resp.message_id  }]);
        pendingMap.current[resp.message_id] = true;
      }
      setMessage('');
      setSelectedFiles([]);
      loadConversations(true);
    } catch (e) { 
      console.error('Send failed', e); 
    } finally { 
      setLoadingSend(false); 
    }
  };

  const saveConversationName = async () => {
    if (!conversationId) return;
    try {
  await chatService.renameConversation(conversationId, conversationName);
      setEditingName(false);
  loadConversations(true);
    } catch (e) { console.error('Rename failed', e); }
  }

  const confirmDeleteConversation = (cid) => {
    setConversationToDelete(cid);
    setShowDeleteModal(true);
  };

  const handleDeleteConversation = async () => {
    if (!conversationToDelete) return;
    setDeleting(true);
    try {
      await chatService.deleteConversation(conversationToDelete);
      // If we're viewing the deleted conversation, clear the view
      if (conversationId === conversationToDelete) {
        clearChat();
      }
      // Refresh conversation list
      await loadConversations(true);
      setShowDeleteModal(false);
      setConversationToDelete(null);
    } catch (e) {
      console.error('Delete failed', e);
      alert('Failed to delete conversation. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const pollPending = async () => {
    const ids = Object.keys(pendingMap.current);
    if (!ids.length) return;
    for (const rid of ids) {
      try {
        const s = await chatService.getResponseStatus(rid);
        if (s.status === 'completed' || s.status === 'error') {
          setMessages(prev => prev.map(m => m.message_id === rid ? { ...m, content: s.response || ('Error: ' + s.error), pending: false } : m));
          delete pendingMap.current[rid];
          loadConversations();
        }
      } catch (e) { console.error('Polling failed', e); }
    }
  };

  // Auto-scroll behavior: when new messages arrive, scroll to bottom only if the
  // user is already near the bottom (so we don't yank the scrollbar while they're
  // looking at earlier messages).
  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    try {
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      const NEAR_BOTTOM_PX = 150; // threshold to decide whether to auto-scroll
      if (distanceFromBottom < NEAR_BOTTOM_PX) {
        // allow DOM to update then scroll
        requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
      }
    } catch (e) { /* ignore */ }
  }, [messages]);

  const handleSelectConversation = (cid) => { loadConversationMessages(cid); };

  const clearChat = () => { setConversationId(null); setMessages([]); setMessage(''); };

  const openComposeDialog = () => {
    // Clear existing conversation context and open compose modal
    setConversationId(null);
    setMessages([]);
    setMessage('');
    setSelectedFiles([]);
    setWizardStep(1);
    setClarifyInput('');
    setEnhancedText('');
    setPendingOriginalMessage('');
    setShowComposeModal(true);
  };

  const closeWizard = () => {
    setShowComposeModal(false);
    setWizardStep(1);
    setClarifyInput('');
    setEnhancedText('');
    setPendingOriginalMessage('');
    modelFreezeRef.current = false;
  };

  const [copiedMessageId, setCopiedMessageId] = useState(null);

  const copyToClipboard = async (text, id) => {
    try {
      if (!text) return;
      await navigator.clipboard.writeText(text);
      setCopiedMessageId(id);
      setTimeout(() => setCopiedMessageId(prev => prev === id ? null : prev), 2000);
    } catch (e) {
      console.error('Copy failed', e);
    }
  };

  const downloadMessage = async (text, messageId, filename) => {
    try {
      if (!text) return;
      
      // Show loading state
      const exportingToast = document.createElement('div');
      exportingToast.className = 'alert alert-info position-fixed';
      exportingToast.style.cssText = 'top: 20px; right: 20px; z-index: 9999;';
      exportingToast.innerHTML = '<div class="spinner-border spinner-border-sm me-2" role="status"></div>Generating PDF...';
      document.body.appendChild(exportingToast);

      // Convert markdown to HTML for PDF export
      const htmlContent = textToHtml(text);
      
      // Call the service to generate PDF
      const pdfBlob = await chatService.exportMessageToPdf(
        messageId,
        htmlContent,
        conversationName || 'Chat Message'
      );

      // Create a download link and trigger download
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || `message-${messageId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      // Remove loading toast and show success
      exportingToast.remove();
      const successToast = document.createElement('div');
      successToast.className = 'alert alert-success position-fixed';
      successToast.style.cssText = 'top: 20px; right: 20px; z-index: 9999;';
      successToast.innerHTML = '<i class="bi bi-check-circle me-2"></i>PDF downloaded successfully!';
      document.body.appendChild(successToast);
      setTimeout(() => successToast.remove(), 3000);
    } catch (e) {
      console.error('Download failed', e);
      
      // Show error toast
      const errorToast = document.createElement('div');
      errorToast.className = 'alert alert-danger position-fixed';
      errorToast.style.cssText = 'top: 20px; right: 20px; z-index: 9999;';
      errorToast.innerHTML = '<i class="bi bi-exclamation-circle me-2"></i>PDF export failed. Please try again.';
      document.body.appendChild(errorToast);
      setTimeout(() => errorToast.remove(), 3000);
    }
  };

  // Filter and sort conversations
  const filteredAndSortedConversations = React.useMemo(() => {
    let result = [...conversations];
    
    // Filter by search term
    if (conversationSearch.trim()) {
      const searchLower = conversationSearch.toLowerCase();
      result = result.filter(c => {
        const name = c.conversation_name || '';
        const preview = c.last_message_preview || '';
        return name.toLowerCase().includes(searchLower) || preview.toLowerCase().includes(searchLower);
      });
    }
    
    // Sort
    if (conversationSort === 'title') {
      result.sort((a, b) => {
        const nameA = (a.conversation_name || a.conversation_id).toLowerCase();
        const nameB = (b.conversation_name || b.conversation_id).toLowerCase();
        return nameA.localeCompare(nameB);
      });
    } else {
      // Sort by date (default) - most recent first
      result.sort((a, b) => b.last_updated - a.last_updated);
    }
    
    return result;
  }, [conversations, conversationSearch, conversationSort]);

  return (
    <div className="container-fluid py-4">
      {showComposeModal && (
        <div className="modal-backdrop" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2050 }}>
          <div className="card" style={{ width: wizardStep === 3 ? '960px' : '800px', maxWidth: '98%' }}>
            <div className="card-header d-flex justify-content-between align-items-center">
              <span>
                {wizardStep === 1 && 'New Conversation'}
                {wizardStep === 2 && `Step 2 of 3: Clarify Your Request`}
                {wizardStep === 3 && `Step 3 of 3: Enhance Your Prompt`}
              </span>
              <button className="btn-close" onClick={closeWizard} aria-label="Close"></button>
            </div>
            <div className="card-body">
              {/* Step 1: Compose */}
              {wizardStep === 1 && (
                <form onSubmit={handleSend}>
                  <div className="row mb-3">
                    <div className="col-md-3">
                      <label className="form-label">Provider</label>
                      <select className="form-select" value={provider} onChange={e => setProvider(e.target.value)}>
                        <option value="openai">OpenAI</option>
                        <option value="anthropic">Anthropic</option>
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Mode</label>
                      <select className="form-select" value={mode} onChange={e => setMode(e.target.value)}>
                        <option value="quick">Quick</option>
                        <option value="deep">Deep</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Model</label>
                      <div className="d-flex">
                        <select className="form-select me-2" value={model} onChange={e => setModel(e.target.value)}>
                          {(provider === 'openai' ? (models[mode]?.openai || []) : (provider === 'anthropic' ? (models[mode]?.anthropic || []) : [])).map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                        <button type="button" className="btn btn-outline-secondary" title="Refresh models" onClick={async () => { chatService.clearModelsCache(); const m = await chatService.getModels(undefined, true); setModels({ quick: { openai: Array.isArray(m.quick?.openai) ? m.quick.openai : [], anthropic: Array.isArray(m.quick?.anthropic) ? m.quick.anthropic : [] }, deep: { openai: Array.isArray(m.deep?.openai) ? m.deep.openai : [], anthropic: Array.isArray(m.deep?.anthropic) ? m.deep.anthropic : [] }, default: m.default || '' }); }}>
                          <i className="bi bi-arrow-clockwise"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Message</label>
                    <textarea className="form-control" rows={6} placeholder="Type your message" value={message} onChange={e => setMessage(e.target.value)} disabled={loadingSend}></textarea>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Context Files (optional)</label>
                    <FileDropzone selectedFiles={selectedFiles} onFilesSelected={files => setSelectedFiles(files)} />
                    {selectedFiles.length > 0 && <div className="form-text">(Files will be encoded and sent inline with the message — small files only!!)</div>}
                    {uploadingFiles && <div className="form-text text-muted">{uploadStatus || 'Uploading files...'}</div>}
                  </div>
                  <div className="d-flex justify-content-end gap-2">
                    <button type="button" className="btn btn-outline-secondary" onClick={closeWizard}>Cancel</button>
                    <button className="btn btn-primary" disabled={loadingSend || uploadingFiles || !message.trim()} type="submit">
                      {loadingSend ? 'Loading...' : (mode === 'deep' ? 'Next: Clarify' : 'Send')}
                    </button>
                  </div>
                </form>
              )}

              {/* Step 2: Clarify (Deep mode only) */}
              {wizardStep === 2 && (
                <div>
                  <div dangerouslySetInnerHTML={{ __html: textToHtml(clarifyQuestions || '') }} />
                  <textarea className="form-control mb-3" rows={4} placeholder="Optional: provide additional details here" value={clarifyInput} onChange={e => setClarifyInput(e.target.value)} />
                  <div className="d-flex justify-content-between">
                    <button className="btn btn-outline-secondary" onClick={goBackInWizard}>
                      <i className="bi bi-arrow-left me-2" />Back
                    </button>
                    <button className="btn btn-primary" onClick={continueSendWithClarification}>
                      Next: Enhance <i className="bi bi-arrow-right ms-2" />
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Enhance (Deep mode only) */}
              {wizardStep === 3 && (
                <div>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <div className="small text-muted mb-0">Original prompt (editable)</div>
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => runEnhancement(message)} disabled={enhancing}>
                          {enhancing ? 'Regenerating…' : 'Regenerate'}
                        </button>
                      </div>
                      <textarea className="form-control" rows={18} value={message} onChange={e => setMessage(e.target.value)} style={{ height: '360px', resize: 'none', overflow: 'auto' }} />
                    </div>
                    <div className="col-md-6">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <div className="small text-muted mb-0">Enhanced prompt</div>
                        <div>
                          <div className="btn-group" role="group" aria-label="enhance view toggle">
                            <button type="button" className={`btn btn-sm ${enhanceView === 'edit' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setEnhanceView('edit')}>Edit</button>
                            <button type="button" className={`btn btn-sm ${enhanceView === 'preview' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setEnhanceView('preview')}>Preview</button>
                          </div>
                        </div>
                      </div>
                      <div style={{ position: 'relative' }}>
                        {enhancing && (
                          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.6)', zIndex: 10 }}>
                            <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
                          </div>
                        )}
                        {enhanceView === 'edit' ? (
                          <textarea className="form-control mb-2" rows={18} value={enhancedText} onChange={e => setEnhancedText(e.target.value)} disabled={enhancing} style={{ height: '360px', resize: 'none', overflow: 'auto' }} />
                        ) : (
                          <div style={{ height: '360px', overflow: 'auto' }} className="border p-2 mb-2">
                            <div dangerouslySetInnerHTML={{ __html: textToHtml(enhancedText || '*No enhancement available yet*') }} />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="d-flex justify-content-between mt-3">
                    <button className="btn btn-outline-secondary" onClick={goBackInWizard}>
                      <i className="bi bi-arrow-left me-2" />Back
                    </button>
                    <div className="d-flex gap-2">
                      <button className="btn btn-outline-secondary" onClick={() => finalSendMessage(false)}>Send Original</button>
                      <button className="btn btn-primary" onClick={() => finalSendMessage(true)} disabled={!enhancedText}>Send Enhanced</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {showDeleteModal && (
        <div className="modal-backdrop" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2050 }}>
          <div className="card" style={{ width: '480px', maxWidth: '95%' }}>
            <div className="card-header">Delete Conversation</div>
            <div className="card-body">
              <p>Are you sure you want to delete this conversation? This action cannot be undone.</p>
              <div className="d-flex justify-content-end gap-2">
                <button className="btn btn-outline-secondary" onClick={() => { setShowDeleteModal(false); setConversationToDelete(null); }} disabled={deleting}>Cancel</button>
                <button className="btn btn-danger" onClick={handleDeleteConversation} disabled={deleting}>
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="row mb-3">
        <div className="col"><h1 className="h3"><i className="bi bi-chat-dots me-2" />Chat Assistant</h1></div>
        <div className="col-auto d-flex align-items-center">
          <button className="btn btn-outline-secondary me-2" onClick={() => setShowConversations(s => !s)} title={showConversations ? 'Hide history' : 'Show history'}>
            <i className="bi bi-list me-2" />{showConversations ? 'Hide History' : 'Show History'}
          </button>
          <button className="btn btn-primary" onClick={openComposeDialog}><i className="bi bi-plus-circle me-2" />New Conversation</button>
        </div>
      </div>
      <div className="row" style={{ height: 'calc(100vh - 210px)' }}>
        {showConversations && (
          <div className="col-lg-3 mb-4" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div className="card" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <div className="card-header d-flex justify-content-between align-items-center" style={{ flexShrink: 0 }}>
                <span>Conversations</span>
                <div>
                  <button className="btn btn-sm btn-outline-primary me-2" onClick={loadConversations} title="Refresh conversations"><i className="bi bi-arrow-clockwise" /></button>
                  <button className="btn btn-sm btn-outline-danger" onClick={() => setShowConversations(false)} title="Hide conversations"><i className="bi bi-x" /></button>
                </div>
              </div>
              <div className="px-3 py-2" style={{ flexShrink: 0, borderBottom: '1px solid #dee2e6' }}>
                <div className="mb-2">
                  <input 
                    type="text" 
                    className="form-control form-control-sm" 
                    placeholder="Search conversations..." 
                    value={conversationSearch}
                    onChange={e => setConversationSearch(e.target.value)}
                  />
                </div>
                <div className="btn-group w-100" role="group" aria-label="Sort conversations">
                  <button 
                    type="button" 
                    className={`btn btn-sm ${conversationSort === 'date' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setConversationSort('date')}
                  >
                    <i className="bi bi-clock me-1" />Date
                  </button>
                  <button 
                    type="button" 
                    className={`btn btn-sm ${conversationSort === 'title' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setConversationSort('title')}
                  >
                    <i className="bi bi-sort-alpha-down me-1" />Title
                  </button>
                </div>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, borderBottomLeftRadius: '0.375rem', borderBottomRightRadius: '0.375rem', overflow: 'hidden' }}>
                <div style={{ overflowY: 'auto', height: '100%', paddingBottom: '2px' }}>
                  <div className="list-group list-group-flush">
                    {filteredAndSortedConversations.map(c => (
                      <button 
                        key={c.conversation_id} 
                        className={`list-group-item list-group-item-action text-start ${c.conversation_id === conversationId ? 'active' : ''}`} 
                        onClick={() => handleSelectConversation(c.conversation_id)} 
                        style={{ border: 'none', borderRadius: 0 }}
                      >
                        <div className="fw-semibold small">{c.conversation_name ? `${c.conversation_name}` : c.conversation_id.substring(0, 8) + '…'}</div>
                        <div className="small text-truncate">{c.last_message_preview}</div>
                        <div className="small text-muted">{formatTimestamp(new Date(c.last_updated * 1000).toISOString())}</div>
                      </button>
                    ))}
                    {filteredAndSortedConversations.length === 0 && conversations.length > 0 && (
                      <div className="p-3 text-muted small">No matching conversations.</div>
                    )}
                    {conversations.length === 0 && <div className="p-3 text-muted small">No conversations yet.</div>}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        <div className={showConversations ? 'col-lg-9' : 'col-lg-12'} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <div className="card chat-card" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div className="card-header d-flex justify-content-between align-items-center" style={{ flexShrink: 0 }}>
              <div>
                {conversationId ? (
                  <div className="d-flex align-items-center">
                    {!editingName ? (
                      <>
                        <strong className="me-2">{conversationName || `Conversation ${conversationId.substring(0, 8)}`}</strong>
                        <button className="btn btn-sm btn-outline-secondary me-2" onClick={() => setEditingName(true)} title="Rename"><i className="bi bi-pencil" /></button>
                        <button className="btn btn-sm btn-outline-danger" onClick={() => confirmDeleteConversation(conversationId)} title="Delete conversation"><i className="bi bi-trash" /></button>
                      </>
                    ) : (
                      <div className="d-flex">
                        <input className="form-control form-control-sm me-2" style={{ width: '220px' }} value={conversationName} onChange={e => setConversationName(e.target.value)} />
                        <button className="btn btn-sm btn-primary me-2" onClick={saveConversationName}>Save</button>
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => { setEditingName(false); loadConversationMessages(conversationId); }}>Cancel</button>
                      </div>
                    )}
                  </div>
                ) : (
                  <span>Conversation</span>
                )}
              </div>
              {conversationId && <span className="badge bg-secondary">{conversationId.substring(0, 8)}</span>}
            </div>
            <div ref={messagesContainerRef} className="card-body chat-messages-container" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
              {messages.length === 0 && <div className="text-muted small">No messages yet.</div>}
              {messages.filter(m => {
                // Filter out hidden system messages (metadata-only messages like renames)
                if (m.role === 'system' && m.meta && m.meta.hidden) return false;
                // Filter out system messages with no content
                if (m.role === 'system' && (!m.content || m.content.trim() === '')) return false;
                return true;
              }).map(m => {
                const isExpanded = expandedMessages[m.message_id] !== false; // Default to expanded
                
                // Helper function to get preview text (first ~2 lines)
                const getPreview = (content) => {
                  if (!content) return '';
                  const lines = content.split('\n').filter(line => line.trim());
                  const previewLines = lines.slice(0, 2);
                  let preview = previewLines.join('\n');
                  if (lines.length > 2) {
                    preview += '...';
                  }
                  return preview;
                };
                
                const contentToShow = isExpanded ? m.content : getPreview(m.content);
                const hasMoreContent = (m.content || '').split('\n').filter(line => line.trim()).length > 2;
                
                return (
                  <div key={m.message_id} className="mb-3">
                    <div className="card">
                      <div 
                        className="card-header d-flex justify-content-between align-items-center"
                        style={{ cursor: hasMoreContent ? 'pointer' : 'default' }}
                        onClick={() => {
                          if (hasMoreContent) {
                            setExpandedMessages(prev => ({
                              ...prev,
                              [m.message_id]: !isExpanded
                            }));
                          }
                        }}
                      >
                        <div className={`fw-bold ${m.role === 'user' ? 'text-primary' : 'text-success'}`}>
                          {m.role === 'user' ? 'You' : `Assistant (${m.model || model})`} 
                          <small className="text-muted ms-2">{new Date((m.created_at || 0) * 1000).toLocaleTimeString()}</small>
                        </div>
                        {hasMoreContent && (
                          <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                        )}
                      </div>
                      <div className="card-body">
                        <div className="message-content">
                          <div dangerouslySetInnerHTML={{ __html: textToHtml(contentToShow || '') }} />
                        </div>
                        {m.pending && <div className="text-warning small mt-2"><span className="spinner-border spinner-border-sm me-1" />Pending deep response…</div>}
                      </div>
                      {m.role !== 'user' && isExpanded && (
                        <div className="card-footer">
                          <div className="d-flex gap-2">
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => copyToClipboard(m.content, m.message_id)}>{copiedMessageId === m.message_id ? 'Copied!' : <><i className="bi bi-clipboard" /> Copy</>}</button>
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => downloadMessage(m.content, m.message_id, `message-${m.message_id}.pdf`)}><i className="bi bi-file-pdf" /> Export PDF</button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {conversationId && (
              <div className="card-footer" style={{ flexShrink: 0 }}>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  if (!message.trim() || loadingSend) return;
                  await finalSendMessage(false);
                }}>
                  <div className="input-group">
                    <textarea 
                      className="form-control" 
                      placeholder="Ask a follow-up question..." 
                      value={message} 
                      onChange={e => setMessage(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          if (message.trim() && !loadingSend) {
                            finalSendMessage(false);
                          }
                        }
                      }}
                      rows={2}
                      disabled={loadingSend}
                      style={{ resize: 'none' }}
                    />
                    <button 
                      className="btn btn-primary" 
                      type="submit" 
                      disabled={!message.trim() || loadingSend}
                    >
                      {loadingSend ? (
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      ) : (
                        <><i className="bi bi-send me-1" /> Send</>
                      )}
                    </button>
                  </div>
                  <div className="form-text small mt-1">Press Enter to send, Shift+Enter for new line</div>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatPage;
